import React, { useEffect, useRef, useState } from 'react';
import { IconInfoRegular } from '@croquiscom-pvt/zds';
import styled from '@emotion/styled';

import { numberWithCommas } from '@common/utils';
import { TouchAccessibilityWrapper } from '@domains/pdp-common';

interface Props {
  price: number;
  priceColor: string;
  text?: string;
  textColor?: string;
  currency: string;
  onClickInfo?: () => void;
}

const minFontSize = 6;
const textLineHeight = 17;
export const StyledPriceTag: React.FC<React.PropsWithChildren<Props>> = ({ price, priceColor, text, textColor, currency, onClickInfo }) => {
  const [priceFontSize, setPriceFontSize] = useState(22);
  const [textFontSize, setTextFontSize] = useState(14);

  const priceRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      const textElement = textRef.current;
      if (textElement) {
        const { clientHeight } = textElement;

        if (clientHeight > textLineHeight && textFontSize >= minFontSize) {
          setTextFontSize((prevFontSize) => prevFontSize - 1);
          setPriceFontSize((prevFontSize) => prevFontSize - 1);
        }
      }
    };

    checkOverflow();
  }, [text, textFontSize, priceFontSize]);

  return (
    <PriceTag>
      <Price ref={priceRef} fontSize={priceFontSize} color={priceColor}>
        {numberWithCommas(price)}
      </Price>
      <Info>
        <Currency fontSize={textFontSize} color={priceColor}>
          {currency}
        </Currency>
        {text && (
          <Text ref={textRef} fontSize={textFontSize} color={textColor}>
            {text}
          </Text>
        )}
        {!!onClickInfo && (
          <TouchAccessibilityWrapper touchWidth={40} touchHeight={40} onClick={onClickInfo}>
            <IconInfoRegular color='textTertiary' size={24} />
          </TouchAccessibilityWrapper>
        )}
      </Info>
    </PriceTag>
  );
};

const PriceTag = styled.div`
  display: flex;
  align-items: center;
  color: ${({ color }) => color};
`;

const Price = styled.div<{ fontSize: number; color?: string }>`
  font-weight: 700;
  max-height: 26px;
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}px`};
  ${({ color }) => color && `color: ${color}`};
`;

const Currency = styled.div<{ fontSize: number; color?: string }>`
  margin: 1px 0 0 1px;
  font-weight: 600;
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}px`};
  ${({ color }) => color && `color: ${color}`};
`;

const Text = styled.div<{ fontSize: number; color?: string }>`
  margin: 1px 0 0 5px;
  line-height: 17px;
  font-weight: 500;
  ${({ fontSize }) => fontSize && `font-size: ${fontSize}px`};
  ${({ color }) => color && `color: ${color}`};
`;

const Info = styled.div`
  position: relative;
  top: 1px;
  display: flex;
  align-items: center;
`;

const WrapIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
